import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Globe2, ShieldCheck, Sparkles, Package, FlaskConical, Truck } from "lucide-react";
import Marquee from "../components/Marquee";
import { INDUSTRIES } from "../lib/constants";

const VALUES = [
  { icon: Globe2, title: "Global Sourcing", desc: "Direct partnerships across Europe, USA, Japan, Korea, and Southeast Asia." },
  { icon: ShieldCheck, title: "Assured Quality", desc: "COA, MSDS and traceability with every consignment — no surprises." },
  { icon: FlaskConical, title: "Technical Support", desc: "In-house formulators available for substitution and application guidance." },
  { icon: Truck, title: "Smooth Imports", desc: "End-to-end logistics, customs clearance, and warehousing handled for you." },
];

const STATS = [
  { v: "18+", l: "Years of industry experience" },
  { v: "120+", l: "Global principal partners" },
  { v: "500+", l: "Raw materials catalogued" },
  { v: "40+", l: "Countries we source from" },
];

export default function HomePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-brand-ink">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/videos/poster.jpg"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source src="/videos/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/90 via-brand-ink/70 to-brand-ink/30" />
        <div className="relative container-x w-full py-28">
          <div className="max-w-3xl animate-fade-up">
            <p className="eyebrow !text-brand-gold mb-6" data-testid="hero-eyebrow">
              Sourcing · Importing · Cosmetic & BPC Raw Materials
            </p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.5rem] text-white leading-[1.02] tracking-tight">
              The <span className="italic text-brand-terracotta">finest ingredients</span>,
              <br />
              curated from the world over.
            </h1>
            <p className="mt-8 text-lg text-white/80 max-w-2xl leading-relaxed">
              Goyam Ingredients connects Indian beauty and personal care
              manufacturers with a meticulously curated network of global
              principals — delivering raw materials that formulators can trust,
              every single batch.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/products" className="btn-primary" data-testid="hero-cta-products">
                Explore Catalogue <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-white border border-white/40 hover:bg-white hover:text-brand-ink px-7 py-4 text-sm tracking-wide rounded-sm transition-all"
                data-testid="hero-cta-contact"
              >
                Talk to our team
              </Link>
            </div>
          </div>
        </div>

        {/* Floating bottom stats */}
        <div className="absolute bottom-0 left-0 right-0 hidden md:block">
          <div className="container-x">
            <div className="bg-brand-surface/90 backdrop-blur-xl border-t border-l border-r border-brand-line grid grid-cols-4">
              {STATS.map((s) => (
                <div key={s.l} className="px-8 py-8 border-r last:border-r-0 border-brand-line/60">
                  <div className="font-serif text-4xl text-brand-moss">{s.v}</div>
                  <div className="text-xs uppercase tracking-[0.2em] mt-2 text-brand-muted">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-y">
        <div className="container-x grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <p className="eyebrow mb-5">Who we are</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-ink leading-tight">
              A quiet backbone of India's beauty & personal care industry.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <p className="text-lg leading-relaxed text-brand-ink/85">
              We source, import, and deliver high-performance cosmetic and BPC
              raw materials — from emulsifiers and surfactants to actives,
              preservatives and botanical extracts. For nearly two decades we've
              been the steady partner behind some of India's most loved beauty
              brands, private labels, and contract manufacturers.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10">
              {VALUES.map((v) => (
                <div
                  key={v.title}
                  data-testid={`value-${v.title.replace(/\s+/g, "-").toLowerCase()}`}
                  className="p-6 bg-brand-surface border border-brand-line hover:border-brand-moss transition-colors"
                >
                  <v.icon className="w-6 h-6 text-brand-terracotta mb-4" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl mb-1.5">{v.title}</h3>
                  <p className="text-sm text-brand-muted leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* Categories preview */}
      <section className="section-y bg-brand-surface/60">
        <div className="container-x">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-14">
            <div className="max-w-2xl">
              <p className="eyebrow mb-4">What we offer</p>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                A catalogue as nuanced as your formulations.
              </h2>
            </div>
            <Link to="/products" className="btn-secondary" data-testid="home-cta-catalog">
              View all products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <Link
              to="/products"
              className="group relative md:col-span-7 aspect-[16/10] overflow-hidden"
              data-testid="feature-botanicals"
            >
              <img
                src="https://images.pexels.com/photos/10181428/pexels-photo-10181428.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Botanical Extracts"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/80 via-transparent" />
              <div className="absolute bottom-0 left-0 p-8 md:p-10 text-white">
                <p className="eyebrow !text-brand-gold mb-3">Featured</p>
                <h3 className="font-serif text-3xl md:text-4xl max-w-md">Botanical Extracts & Actives</h3>
                <p className="mt-2 text-white/75 text-sm max-w-md">
                  Standardized actives with complete traceability, from centella
                  to niacinamide.
                </p>
              </div>
            </Link>

            <div className="md:col-span-5 grid grid-cols-1 gap-6">
              <Link
                to="/products"
                className="group relative aspect-[16/9] overflow-hidden"
                data-testid="feature-fragrances"
              >
                <img
                  src="https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?auto=format&fit=crop&w=1400&q=80"
                  alt="Essential Oils & Fragrances"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-brand-ink/40 group-hover:bg-brand-ink/55 transition" />
                <div className="absolute bottom-0 left-0 p-6 text-white">
                  <h3 className="font-serif text-2xl">Essential Oils & Fragrances</h3>
                </div>
              </Link>
              <Link
                to="/products"
                className="group relative aspect-[16/9] overflow-hidden"
                data-testid="feature-emollients"
              >
                <img
                  src="https://images.pexels.com/photos/6689399/pexels-photo-6689399.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Emollients"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-brand-ink/40 group-hover:bg-brand-ink/55 transition" />
                <div className="absolute bottom-0 left-0 p-6 text-white">
                  <h3 className="font-serif text-2xl">Colorants & Emollients</h3>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section-y">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5">
              <p className="eyebrow mb-4">Industries we serve</p>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                Trusted across the beauty & personal care spectrum.
              </h2>
              <p className="mt-6 text-brand-muted leading-relaxed">
                From heritage Ayurvedic brands to modern clinical skincare
                labs — our materials power formulations at every scale.
              </p>
            </div>
            <div className="md:col-span-7">
              <div className="flex flex-wrap gap-3">
                {INDUSTRIES.map((ind) => (
                  <span
                    key={ind}
                    data-testid={`industry-${ind.replace(/\s+/g, "-").toLowerCase()}`}
                    className="px-5 py-3 border border-brand-line bg-brand-surface text-sm tracking-wide hover:border-brand-terracotta hover:text-brand-terracotta transition-colors"
                  >
                    {ind}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-y bg-brand-ink text-white">
        <div className="container-x">
          <p className="eyebrow !text-brand-gold mb-4">How we work</p>
          <h2 className="font-serif text-4xl md:text-5xl mb-16 max-w-3xl">
            A simple, honest process — built around your formulation deadlines.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {[
              { n: "01", t: "Discover", d: "Share your target specs, application, and documentation needs." },
              { n: "02", t: "Curate", d: "We propose options from our principal network with COAs and samples." },
              { n: "03", t: "Import", d: "We handle logistics, customs and QC — you simply receive stock." },
              { n: "04", t: "Support", d: "On-call technical support for substitution, scale-up and compliance." },
            ].map((s) => (
              <div key={s.n} className="relative pl-6 border-l border-white/20">
                <div className="font-serif text-brand-gold text-3xl mb-3">{s.n}</div>
                <h3 className="font-serif text-2xl mb-2">{s.t}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-y">
        <div className="container-x">
          <div className="relative overflow-hidden bg-brand-surfaceAlt p-12 md:p-20">
            <Sparkles className="absolute top-8 right-8 w-12 h-12 text-brand-terracotta/60" strokeWidth={1} />
            <div className="max-w-2xl">
              <p className="eyebrow mb-4">Ready to source?</p>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-ink leading-tight">
                Tell us what you're formulating. We'll find the right ingredient.
              </h2>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/contact" className="btn-primary" data-testid="cta-contact">
                  Start a conversation
                </Link>
                <Link to="/products" className="btn-secondary" data-testid="cta-browse">
                  Browse catalogue
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
